import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { FaCalendarAlt, FaTag, FaUser, FaArrowRight, FaSearch } from 'react-icons/fa';
import { apiService } from '../services/api';
import LoadingSpinner from '../components/common/LoadingSpinner';

const Blog = () => {
  const [posts, setPosts] = useState([]);
  const [filteredPosts, setFilteredPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');

  useEffect(() => {
    const fetchPosts = async () => {
      try {
        setLoading(true);
        const response = await apiService.getBlogPosts();
        console.log('Public blog response:', response.data);
        
        let postsData = [];
        if (Array.isArray(response.data)) {
          postsData = response.data;
        }
        
        // Parse tags
        postsData = postsData.map(post => {
          let tags = post.tags || [];
          if (typeof tags === 'string') {
            try {
              tags = JSON.parse(tags) || [];
            } catch {
              tags = [];
            }
          }
          if (!Array.isArray(tags)) {
            tags = [];
          }
          return { ...post, tags };
        });
        
        setPosts(postsData);
        setFilteredPosts(postsData);
      } catch (error) {
        console.error('Error fetching blog posts:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchPosts();
  }, []);

  useEffect(() => {
    let filtered = posts;
    
    if (searchTerm) {
      filtered = filtered.filter(post => 
        post.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        post.excerpt.toLowerCase().includes(searchTerm.toLowerCase()) ||
        post.tags?.some(tag => tag.toLowerCase().includes(searchTerm.toLowerCase()))
      );
    }
    
    if (selectedCategory !== 'all') {
      filtered = filtered.filter(post => post.category === selectedCategory);
    }
    
    setFilteredPosts(filtered);
  }, [searchTerm, selectedCategory, posts]);

  const categories = ['all', ...new Set(posts.map(post => post.category))];

  const formatDate = (date) => {
    return new Date(date).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <LoadingSpinner size="large" />
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-20 px-4 sm:px-6 lg:px-8">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-12"
        >
          <span className="inline-block px-4 py-2 rounded-full bg-primary-500/10 border border-primary-500/20 text-primary-400 text-sm font-medium mb-4">
            Blog
          </span>
          <h1 className="text-4xl sm:text-5xl font-bold">
            Thoughts & <span className="gradient-text">Insights</span>
          </h1>
          <p className="text-gray-400 mt-4 max-w-2xl mx-auto">
            Articles, tutorials, and engineering thoughts from my journey
          </p>
        </motion.div>

        {/* Search & Filter */}
        <div className="flex flex-wrap items-center gap-4 mb-8">
          <div className="flex-1 min-w-[200px]">
            <div className="relative">
              <FaSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" />
              <input
                type="text"
                placeholder="Search articles..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 bg-white/5 border border-white/10 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-primary-500"
              />
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            {categories.map((category) => (
              <button
                key={category}
                onClick={() => setSelectedCategory(category)}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-all duration-300 capitalize ${
                  selectedCategory === category
                    ? 'bg-primary-500 text-white shadow-lg shadow-primary-500/30'
                    : 'bg-white/5 text-gray-400 hover:bg-white/10 hover:text-white'
                }`}
              >
                {category}
              </button>
            ))}
          </div>
        </div>

        {/* Blog Posts */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredPosts.map((post, index) => (
            <motion.article
              key={post.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
              className="glass rounded-2xl overflow-hidden hover:bg-white/10 transition-all duration-300 group"
            >
              {post.featured_image && (
                <div className="h-48 overflow-hidden">
                  <img 
                    src={post.featured_image} 
                    alt={post.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                </div>
              )}
              
              <div className="p-6">
                <div className="flex flex-wrap items-center gap-3 text-xs text-gray-400 mb-3">
                  <span className="flex items-center gap-1">
                    <FaCalendarAlt />
                    {formatDate(post.published_at)}
                  </span>
                  {post.category && (
                    <span className="flex items-center gap-1">
                      <FaTag />
                      {post.category}
                    </span>
                  )}
                </div>

                <Link to={`/blog/${post.slug}`}>
                  <h2 className="text-xl font-semibold text-white mb-2 group-hover:text-primary-400 transition-colors duration-300 line-clamp-2">
                    {post.title}
                  </h2>
                </Link>
                
                <p className="text-gray-400 text-sm mb-4 line-clamp-2">
                  {post.excerpt}
                </p>

                <div className="flex flex-wrap items-center justify-between">
                  <div className="flex flex-wrap gap-2">
                    {post.tags && post.tags.slice(0, 2).map((tag, i) => (
                      <span 
                        key={i}
                        className="px-2 py-1 text-xs rounded-md bg-white/5 border border-white/10 text-gray-400"
                      >
                        #{tag}
                      </span>
                    ))}
                  </div>
                  
                  <Link 
                    to={`/blog/${post.slug}`}
                    className="flex items-center gap-1 text-sm text-primary-400 hover:text-primary-300 transition-colors duration-300"
                  >
                    Read More
                    <FaArrowRight size={12} />
                  </Link>
                </div>
              </div>
            </motion.article>
          ))}
        </div>

        {filteredPosts.length === 0 && (
          <div className="text-center py-12">
            <p className="text-gray-400">No blog posts found matching your criteria.</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default Blog;